import * as React from "react"
import { gql } from "@apollo/client"
import { Box, Button, Center, SimpleGrid, Spinner, Stack, Text } from "@chakra-ui/react"
import Head from "next/head"

import { paginate } from "lib/apollo/helpers"
import { SortOrder, useGetTutorialsQuery } from "lib/graphql"
import { useLocalStorage } from "lib/hooks/useLocalStorage"
import { Layout } from "components/Layout"
import { Limiter } from "components/Limiter"
import { TutorialFilters, TutorialsFilterbar } from "components/TutorialsFilterbar"
import { TutorialTile } from "components/TutorialTile"

const _ = gql`
  fragment TutorialItem on Tutorial {
    id
    videoUrl
    title
    duration
    instructor
    categories
    isLive
    createdAt
    hasCompleted
    difficultyRating
  }

  query GetTutorials(
    $take: Int
    $orderBy: [TutorialOrderByWithRelationInput!]
    $where: TutorialWhereInput
    $skip: Int
  ) {
    tutorials(take: $take, orderBy: $orderBy, where: $where, skip: $skip) {
      items {
        ...TutorialItem
      }
      count
    }
  }
`

const TAKE = 10
export default function Tutorials() {
  const [filters, setFilters] = useLocalStorage<TutorialFilters>("kunstbende.filters.tutorials", {
    category: "",
  })
  const { data, loading, fetchMore } = useGetTutorialsQuery({
    variables: {
      orderBy: { createdAt: SortOrder.Desc },
      take: TAKE,
      where: {
        isLive: { equals: true },
        categories: filters.category ? { has: filters.category } : undefined,
      },
    },
  })

  const handleFetchMore = () => {
    if (!tutorials) return
    return fetchMore({
      variables: { skip: tutorials.length, take: TAKE },
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      updateQuery: paginate("tutorials"),
    })
  }

  const tutorials = data?.tutorials.items
  const tutorialCount = data?.tutorials.count

  return (
    <>
      <Head>
        <title>Kunstbende - Tutorials</title>
      </Head>
      <Limiter>
        <Stack w="100%" mb={{ base: 40, lg: 40 }} spacing={{ base: 4, md: 4 }} mt={16} h="100%">
          <Text
            fontSize={{ base: 32, md: 56 }}
            fontWeight={900}
            lineHeight={{ base: "shorter", md: "short" }}
            px={0}
            textAlign="left"
            role="heading"
            aria-level={1}
          >
            Tutorials
          </Text>
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
            <TutorialsFilterbar filters={filters} onSetFilters={setFilters} />
          </SimpleGrid>
          {loading ? (
            <Center h={300}>
              <Spinner />
            </Center>
          ) : !!!tutorials ? (
            <Center h={300}>
              <Text fontWeight="bold" fontSize="2xl">
                Fout bij ophalen van tutorials
              </Text>
            </Center>
          ) : tutorials.length === 0 ? (
            <Center h={300}>
              <Text fontWeight="bold" fontSize="2xl">
                Geen tutorials gevonden
              </Text>
            </Center>
          ) : (
            <Box pt={6} role="main" aria-label="Tutorials overzicht">
              <Stack spacing={8}>
                {tutorials.map((tutorial) => (
                  <TutorialTile key={tutorial.id} tutorial={tutorial} />
                ))}
              </Stack>
              {tutorialCount && tutorials.length < tutorialCount ? (
                <Center pt={12}>
                  <Button onClick={handleFetchMore} colorScheme="yellow">
                    Meer laden
                  </Button>
                </Center>
              ) : null}
            </Box>
          )}
        </Stack>
      </Limiter>
    </>
  )
}

Tutorials.getLayout = (page: React.ReactElement) => <Layout>{page}</Layout>
